import { BlockRecord } from "models/view-models/block-record";
import { useCallback } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import {
    SortItemMovement,
    useSortedRecords,
} from "utilities/hooks/domain/use-sorted-records";
import { useLocalization } from "utilities/hooks/use-localization";
import BlockService, {
    BlockPathParams,
    MoveBlockPathParams,
} from "utilities/services/blocks/block-service";
import { ToastManager } from "utilities/toast/toast-manager";
import { useReadonlyContext } from "utilities/contexts/readonly/use-readonly-context";

export interface UseSortBlocks {
    onBlocksReordered: (updatedBlocks: BlockRecord[]) => Promise<number[]>;
    sortedBlocks: BlockRecord[];
}

export const useSortBlocks = (blocks: BlockRecord[]): UseSortBlocks => {
    const { patch: moveApi } = BlockService.useMove();
    const { t } = useLocalization();
    const { records, onRecordsMoved, resetRecordSort } =
        useSortedRecords<BlockRecord>(blocks, BlockRecord);
    const { skipIfIsReadonly } = useReadonlyContext();

    const makeMoveApiCall = useCallback(
        async (
            movePathParams: BlockPathParams,
            move: MoveBlockPathParams
        ): Promise<number[]> => {
            try {
                const moveResponse = await moveApi(movePathParams, move);

                if (moveResponse.result?.hasErrors()) {
                    throw new Error(
                        moveResponse.result?.listErrorMessages().join(", ")
                    );
                }

                if (moveResponse.resultObject == null) {
                    throw new Error("No block IDs received on move.");
                }

                return moveResponse.resultObject[0];
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("moving"),
                        resource: t("block"),
                    })
                );

                return resetRecordSort();
            }
        },
        [moveApi, resetRecordSort, t]
    );

    const moveBlocks = useCallback(
        async (moves: MoveBlockPathParams[]): Promise<number[]> => {
            if (CollectionUtils.isEmpty(moves)) {
                return [];
            }

            let blockIds: number[] = [];

            for (const move of moves) {
                blockIds = await makeMoveApiCall({}, move);
            }

            return blockIds;
        },
        [makeMoveApiCall]
    );

    const onBlocksReordered = useCallback(
        async (updatedBlocks: BlockRecord[]): Promise<number[]> => {
            const diffs = onRecordsMoved(updatedBlocks);
            const moves = diffsToMoves(diffs);

            return await moveBlocks(moves);
        },
        [moveBlocks, onRecordsMoved]
    );

    return {
        onBlocksReordered: skipIfIsReadonly(onBlocksReordered),
        sortedBlocks: records,
    };
};

const diffsToMoves = (diffs: SortItemMovement[]): MoveBlockPathParams[] => {
    return diffs.map(diffToMove.bind(null));
};

const diffToMove = (diff: SortItemMovement): MoveBlockPathParams => ({
    movingBlockId: diff.movingRecordId,
    precedingBlockId: diff.precedingRecordId,
});
